import {
  DeleteOutlined,
  FileExcelOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import * as XLSX from "xlsx";
import { EXCHANGE_RATE } from "../../constants/common";
import { PriorityMark } from "../../enum/common";
import { ISuppliesLogistics } from "../../models/Logistics";
import {
  ICreateResearchPurchaseRequest,
  IListMaterial,
} from "../../models/Research";
import { useSearchSupplierLogistics } from "../../query/LogisticsQuery";
import { useImportListRequest } from "../../query/ProductionQuery";
import { useCreatePurchaseRequest } from "../../query/ReseachQuery";
import { notify } from "../../utils/toast";

const { TextArea } = Input;
const { Text } = Typography;

interface DrawerCreatePurchaseRequestProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  budgetCategories: Array<{
    id: number;
    categoryName: string;
    budgetedAmount: string;
    spentAmount: string;
  }>;
  onSubmit: (data: ICreateResearchPurchaseRequest) => void;
  loading?: boolean;
}

const DrawerCreatePurchaseRequest = ({
  open,
  onClose,
  projectId,
  budgetCategories,
}: DrawerCreatePurchaseRequestProps) => {
  const [form] = Form.useForm();
  const exchangeRate = Form.useWatch("exchangeRate", form) || 0;
  const items = Form.useWatch("listMaterials", form) || [];
  const [searchCode, setSearchCode] = useState<string>("");
  const [supplierData, setSupplierData] = useState<ISuppliesLogistics | null>(
    null,
  );

  const createResearchPurchaseRequest = useCreatePurchaseRequest();
  const { data: searchResult } = useSearchSupplierLogistics(searchCode);
  const importListRequest = useImportListRequest();

  React.useEffect(() => {
    if (searchResult) {
      setSupplierData(searchResult);
    }
  }, [searchResult]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value && value.length >= 2) {
        setSearchCode(value);
      } else {
        setSearchCode("");
      }
    }, 300),
    [],
  );

  const handleInternalCodeChange = (value: string) => {
    debouncedSearch(value);
  };

  const handleInternalCodeBlur = (fieldName: number) => {
    const internalCode = form.getFieldValue([
      "listMaterials",
      fieldName,
      "internalCode",
    ]);

    if (supplierData && supplierData.internalCode === internalCode) {
      form.setFieldsValue({
        listMaterials: {
          [fieldName]: {
            partNumber: supplierData.partNumber,
            manufacturer: supplierData.manufacturer,
            description: supplierData.description,
            unit: supplierData.unit,
            importTax: supplierData.importTax,
          },
        },
      });
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        internalCode: "",
        datePr: "",
        category: "",
        quantity: "",
        source: "",
        crd: "",
        unitPriceVnd: "",
        shippingVnd: "",
        link: "",
        projectCode: "",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "purchase_request_template.xlsx");
  };

  const handleUploadExcel = async (file: File) => {
    try {
      const result = await importListRequest.mutateAsync(file);

      if (result && Array.isArray(result)) {
        const currentItems = form.getFieldValue("listMaterials") || [];
        const newItems = result.map((item: any) => ({
          internalCode: item.internalCode || "",
          datePr: item.datePr ? dayjs(item.datePr) : dayjs(),
          category: item.category || "",
          quantity: item.quantity || 1,
          source: item.source || "",
          crd: item.crd ? dayjs(item.crd) : dayjs(),
          unitPriceVnd: item.unitPriceVnd || 0,
          shippingVnd: item.shippingVnd || 0,
          link: item.link || "",
          partNumber: item.partNumber || "",
          manufacturer: item.manufacturer || "",
          description: item.description || "",
          unit: item.unit || "",
          importTax: item.importTax || 0,
          projectCode: item.projectCode || "",
        }));

        form.setFieldsValue({
          listMaterials: [...currentItems, ...newItems],
        });
        notify.success("Upload file thành công");
      }
    } catch (error: any) {
      notify.error(error.response.data.message);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadExcel(file);
      e.target.value = "";
    }
  };

  const calculateTotalProposal = () => {
    return items.reduce((sum: number, item: IListMaterial) => {
      if (!item) return sum;
      const unitPrice = item.unitPriceVnd || 0;
      const quantity = item.quantity || 0;
      const shipping = item.shippingVnd || 0;
      const importTax = item.importTax || 0;
      const totalVnd = unitPrice * quantity;
      const totalImportTax = (totalVnd * importTax) / 100;
      return sum + totalVnd + shipping + totalImportTax;
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data: ICreateResearchPurchaseRequest = {
        projectId: projectId,
        requestBy: values.requestBy,
        note: values.note,
        budgetCategoryId: values.budgetCategoryId,
        exchangeRate: values.exchangeRate,
        priority: values.priority,
        listMaterials: values.listMaterials.map((item: any) => ({
          datePr: item.datePr.format("YYYY-MM-DD"),
          partNumber: item.partNumber,
          internalCode: item.internalCode || "",
          category: item.category || "",
          manufacturer: item.manufacturer || "",
          description: item.description || "",
          unit: item.unit,
          quantity: item.quantity,
          source: item.source || "",
          crd: item.crd.format("YYYY-MM-DD"),
          unitPriceVnd: item.unitPriceVnd,
          shippingVnd: item.shippingVnd || 0,
          link: item.link || "",
          importTax: item.importTax || 0,
          projectCode: item.projectCode || "",
        })),
      };
      await createResearchPurchaseRequest.mutateAsync(data);
      notify.success("Tạo yêu cầu mua hàng thành công");
      form.resetFields();
      onClose();
    } catch (error) {
      notify.error("Tạo yêu cầu mua hàng thất bại");
    }
  };

  return (
    <Drawer
      title="Tạo yêu cầu mua hàng"
      placement="right"
      width="100%"
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      open={open}
      extra={
        <Space>
          <Text strong className="text-lg text-blue-600">
            Tổng: {calculateTotalProposal().toLocaleString("vi-VN")} VND
          </Text>
          <Button onClick={onClose}>Hủy</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={createResearchPurchaseRequest.isPending}
          >
            Tạo yêu cầu
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ priority: PriorityMark.LOW, listMaterials: [] }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Ngày yêu cầu"
              name="requestDate"
              rules={[{ required: true, message: "Vui lòng chọn ngày" }]}
              initialValue={dayjs()}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Người yêu cầu"
              name="requestBy"
              rules={[
                { required: true, message: "Vui lòng nhập người yêu cầu" },
              ]}
            >
              <Input
                placeholder="Nhập tên người yêu cầu"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label="Danh mục ngân sách"
              name="budgetCategoryId"
              rules={[
                { required: true, message: "Vui lòng chọn danh mục ngân sách" },
              ]}
            >
              <Select placeholder="Chọn danh mục" style={{ width: "100%" }}>
                {budgetCategories.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.categoryName} (Còn lại:{" "}
                    {(
                      parseFloat(category.budgetedAmount) -
                      parseFloat(category.spentAmount)
                    ).toLocaleString("vi-VN")}{" "}
                    VND)
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Tỉ giá (VND/USD)"
              name="exchangeRate"
              rules={[{ required: true, message: "Vui lòng nhập tỉ giá" }]}
              initialValue={EXCHANGE_RATE}
            >
              <InputNumber
                placeholder="Nhập tỉ giá"
                style={{ width: "100%" }}
                min={0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  Number(value!.replace(/\$\s?|(,*)/g, "")) as any
                }
              />
            </Form.Item>
            <Form.Item
              label="Mức độ ưu tiên"
              name="priority"
              rules={[{ required: true, message: "Vui lòng chọn mức độ" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Chọn mức độ">
                <Select.Option value={PriorityMark.LOW}>Thường</Select.Option>
                <Select.Option value={PriorityMark.MEDIUM}>Gấp</Select.Option>
                <Select.Option value={PriorityMark.HIGH}>Rất gấp</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Ghi chú" name="note">
          <TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
        </Form.Item>

        <Form.List name="listMaterials">
          {(fields, { add, remove }) => {
            const columns = [
              {
                title: "Mã nội bộ",
                dataIndex: "internalCode",
                key: "internalCode",
                width: 120,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "internalCode"]}
                    style={{ margin: 0 }}
                  >
                    <Input
                      placeholder="Mã"
                      size="small"
                      onChange={(e) => handleInternalCodeChange(e.target.value)}
                      onBlur={() => handleInternalCodeBlur(record.name)}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Mã dự án",
                dataIndex: "projectCode",
                key: "projectCode",
                width: 120,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "projectCode"]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Mã dự án" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: (
                  <span>
                    Ngày PR <span className="text-red-500">*</span>
                  </span>
                ),
                dataIndex: "datePr",
                key: "datePr",
                width: 130,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "datePr"]}
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      size="small"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                ),
              },
              {
                title: (
                  <span>
                    Part Number <span className="text-red-500">*</span>
                  </span>
                ),
                dataIndex: "partNumber",
                key: "partNumber",
                width: 150,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "partNumber"]}
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input disabled placeholder="Part Number" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: "Danh mục",
                dataIndex: "category",
                key: "category",
                width: 120,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "category"]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Danh mục" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: "Nhà SX",
                dataIndex: "manufacturer",
                key: "manufacturer",
                width: 120,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "manufacturer"]}
                    style={{ margin: 0 }}
                  >
                    <Input disabled placeholder="Nhà SX" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: "Mô tả",
                dataIndex: "description",
                key: "description",
                width: 150,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "description"]}
                    style={{ margin: 0 }}
                  >
                    <Input disabled placeholder="Mô tả" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: (
                  <span>
                    ĐVT <span className="text-red-500">*</span>
                  </span>
                ),
                dataIndex: "unit",
                key: "unit",
                width: 80,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "unit"]}
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input disabled placeholder="ĐVT" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: (
                  <span>
                    SL <span className="text-red-500">*</span>
                  </span>
                ),
                dataIndex: "quantity",
                key: "quantity",
                width: 80,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "quantity"]}
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      placeholder="SL"
                      size="small"
                      min={1}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Nguồn",
                dataIndex: "source",
                key: "source",
                width: 100,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "source"]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Nguồn" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: "CRD",
                dataIndex: "crd",
                key: "crd",
                width: 130,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "crd"]}
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: "" }]}
                  >
                    <DatePicker
                      placeholder="CRD"
                      size="small"
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                ),
              },
              {
                title: (
                  <span>
                    Đơn giá (VND) <span className="text-red-500">*</span>
                  </span>
                ),
                dataIndex: "unitPriceVnd",
                key: "unitPriceVnd",
                width: 130,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "unitPriceVnd"]}
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      placeholder="Đơn giá"
                      size="small"
                      min={0}
                      style={{ width: "100%" }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) =>
                        Number(value!.replace(/\$\s?|(,*)/g, "")) as any
                      }
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Đơn giá USD",
                key: "unitPriceUsd",
                width: 100,
                render: (_: any, record: any) => {
                  const item = items[record.name] || {};
                  const unitPriceVnd = item.unitPriceVnd || 0;
                  const unitPriceUsd =
                    exchangeRate > 0 ? unitPriceVnd / exchangeRate : 0;
                  return (
                    <Text className="text-blue-600 text-xs">
                      ${unitPriceUsd.toFixed(2)}
                    </Text>
                  );
                },
              },
              {
                title: "Phí ship (VND)",
                dataIndex: "shippingVnd",
                key: "shippingVnd",
                width: 130,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "shippingVnd"]}
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: "" }]}
                  >
                    <InputNumber
                      placeholder="Phí ship"
                      size="small"
                      min={0}
                      style={{ width: "100%" }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) =>
                        Number(value!.replace(/\$\s?|(,*)/g, "")) as any
                      }
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Thuế NK (%)",
                dataIndex: "importTax",
                key: "importTax",
                width: 100,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "importTax"]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      placeholder="Thuế"
                      size="small"
                      min={0}
                      max={100}
                      disabled
                      style={{ width: "100%" }}
                      formatter={(value) => `${value}%`}
                      parser={(value) => Number(value!.replace("%", "")) as any}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Tổng thuế NK",
                key: "totalImportTax",
                width: 120,
                render: (_: any, record: any) => {
                  const item = items[record.name] || {};
                  const unitPrice = item.unitPriceVnd || 0;
                  const quantity = item.quantity || 0;
                  const importTax = item.importTax || 0;
                  const totalVnd = unitPrice * quantity;
                  const totalImportTax = (totalVnd * importTax) / 100;
                  return (
                    <Text className="text-orange-600">
                      {totalImportTax.toLocaleString("vi-VN")}
                    </Text>
                  );
                },
              },
              {
                title: "Tổng VND",
                key: "totalVnd",
                width: 120,
                render: (_: any, record: any) => {
                  const item = items[record.name] || {};
                  const unitPrice = item.unitPriceVnd || 0;
                  const quantity = item.quantity || 0;
                  const totalVnd = unitPrice * quantity;
                  return (
                    <Text className="text-green-600">
                      {totalVnd.toLocaleString("vi-VN")}
                    </Text>
                  );
                },
              },
              {
                title: "Link",
                dataIndex: "link",
                key: "link",
                width: 150,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "link"]}
                    style={{ margin: 0 }}
                    rules={[
                      {
                        type: "url",
                        message: "Link không hợp lệ",
                      },
                    ]}
                  >
                    <Input placeholder="Link" size="small" />
                  </Form.Item>
                ),
              },
              {
                title: "Tổng cộng",
                key: "total",
                fixed: "right" as const,
                width: 150,
                render: (_: any, record: any) => {
                  const item = items[record.name] || {};
                  const unitPrice = item.unitPriceVnd || 0;
                  const quantity = item.quantity || 0;
                  const shipping = item.shippingVnd || 0;
                  const importTax = item.importTax || 0;
                  const totalVnd = unitPrice * quantity;
                  const totalImportTax = (totalVnd * importTax) / 100;
                  const grandTotal = totalVnd + shipping + totalImportTax;
                  const totalUsd =
                    exchangeRate > 0 ? grandTotal / exchangeRate : 0;

                  return (
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        ${totalUsd.toFixed(2)}
                      </div>
                      <Text strong className="text-blue-600">
                        {grandTotal.toLocaleString("vi-VN")} ₫
                      </Text>
                    </div>
                  );
                },
              },
              {
                title: "",
                key: "action",
                width: 50,
                fixed: "right" as const,
                render: (_: any, record: any) => (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(record.name)}
                  />
                ),
              },
            ];

            return (
              <>
                <div className="mb-2 flex text-center gap-2">
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    loading={createResearchPurchaseRequest.isPending}
                    icon={<PlusOutlined />}
                  >
                    Thêm vật tư
                  </Button>
                  <Button
                    block
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={() =>
                      document.getElementById("excel-upload")?.click()
                    }
                    loading={importListRequest.isPending}
                  >
                    Upload Excel
                  </Button>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    block
                    color="green"
                    variant="outlined"
                    icon={<FileExcelOutlined />}
                    onClick={handleDownloadTemplate}
                  >
                    Excel mẫu
                  </Button>
                </div>

                <Table
                  columns={columns}
                  dataSource={fields}
                  pagination={false}
                  scroll={{ x: 2400, y: 500 }}
                  size="small"
                  bordered
                  rowKey="key"
                />
              </>
            );
          }}
        </Form.List>
      </Form>
    </Drawer>
  );
};

export default DrawerCreatePurchaseRequest;
